import React, { useState, useEffect, useRef, LegacyRef } from "react";
import ReactDatePicker, { registerLocale } from "react-datepicker";
import { DateTime } from "luxon";
import { toPickerDate } from "./pickerDate";
import styles from "./styles.module.scss";
import i18n from "app/i18n";
import { schedulingFetchGet } from "app/utils/request/scheduling/fetch";
import { CoachProfileModel } from "app/models/CoachProfileModel";
import { castArray, isNil, isEmpty } from 'lodash';

import en from "date-fns/locale/en-US";
import ja from "date-fns/locale/ja";
import { cls } from "app/utils";
import useCalendarPreferences from "app/utils/hooks/useCalendarPreferences";

const locales = {
  en,
  ja,
};

type PropsDef = {
  date: DateTime;
  setDate: (date: DateTime) => void;
  formatDate?: (date: DateTime) => string;
  minDate?: Date | null;
  coach?: CoachProfileModel;
  minutesDuration?: number;
  pricingValue?: any;
  inline?: boolean;
  mobile?: boolean;
  'data-testid'?: string;
};

const defaultDateFormat = (date: DateTime): string => {
  return date.setLocale("en-us").toLocaleString(DateTime.DATE_FULL);
};

const DatePicker = (props: PropsDef) => {
  const {
    date,
    setDate,
    formatDate = defaultDateFormat,
    minDate = null,
    coach,
    minutesDuration,
    pricingValue,
    inline = false,
    mobile = false,
  } = props;
  const { preferences } = useCalendarPreferences();
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [excludeDates, setExcludeDates] = useState<Date[]>([]);
  const closing = useRef(false);
  const [actualDate, setActualDate] = useState(date);

  let coaches: CoachProfileModel[] | null;

  if (isNil(coach)) {
    coaches = [];
  } else {
    coaches = castArray(coach);
  }

  const fetchExcludedDates = async (currentDate: DateTime) => {
    if (isEmpty(coaches)) {
      return;
    }

    try {
      const params = {
        from: DateTime.local(
          currentDate.year,
          currentDate.month,
          1,
        ).toISODate(),
        to: DateTime.local(
          currentDate.year,
          currentDate.month,
          currentDate.daysInMonth,
        ).toISODate(),
        coach_profile_ids: coaches.map(c => c.id),
        slot_duration: minutesDuration,
        lesson_rate_id: pricingValue?.id,
      };

      const data = await schedulingFetchGet(
        "/available_dates",
        params,
        {},
        null,
        true,
      );

      const allDatesInMonth = Array.from(
        { length: currentDate.daysInMonth },
        (_, i) =>
          DateTime.local(
            currentDate.year,
            currentDate.month,
            i + 1,
          ).toISODate(),
      );

      // If API returns empty array, no dates are available - exclude all dates
      if (!data || data.length === 0) {
        const formattedDates = allDatesInMonth.map((day: string): Date =>
          DateTime.fromISO(day).toJSDate()
        );
        setExcludeDates(formattedDates);
        return;
      }

      if (!data[0].dates) {
        data[0].dates = [];
      }

      const datesArray = data.flatMap((profile: any): DateTime[] => {
        if (!profile.dates) {
          return [];
        }

        return profile.dates.map((dateString: string): string =>
          DateTime.fromISO(dateString).toISODate()
        );
      });

      const availableDates = new Set(datesArray);

      const formattedDates = allDatesInMonth
        .filter((day: string): boolean => !availableDates.has(day))
        .map((day: string): Date => DateTime.fromISO(day).toJSDate());

      setExcludeDates(formattedDates);
    } catch (error) {
      console.error("Error fetching excluded dates:", error);
    }
  };

  useEffect((): void => {
    if (isEmpty(coaches)) {
      return;
    }

    fetchExcludedDates(date);
  }, [date, JSON.stringify(coaches.map(c => c.id)), pricingValue?.id]);

  useEffect((): void => {
    const currentLocale = locales[i18n.language] || locales.en;
    registerLocale(i18n.language, currentLocale);
  }, [i18n.language]);

  const CustomInput = React.forwardRef((_props, ref) => {
    return (
      <div
        className={styles.title_container}
        onClick={handleClick}
        ref={ref as LegacyRef<HTMLInputElement>}
        data-testid={props['data-testid']}
      >
        <div className={styles.title}>{formatDate(date)}</div>
        <div
          className={styles.offset}
          style={{ transform: `${datePickerOpen ? "rotate(180deg)" : "none"}` }}
        >
          <i className="ico ico-chevron" />
        </div>
      </div>
    );
  });
  CustomInput.displayName = "HeaderInput";

  const CustomMobileInput = React.forwardRef((_props, ref) => {
    return (
      <div
        className={styles.mobile_calendar_button}
        onClick={handleClick}
        ref={ref as LegacyRef<HTMLInputElement>}
      >
        <i className="ico ico-calendar" />
      </div>
    );
  });

  const handleChange = (value: Date) => {
    const localDate = DateTime.fromJSDate(value).startOf("day");
    setDate(localDate);
    setActualDate(localDate);
    setDatePickerOpen(false);
  };

  const handleClick = (e: any): void => {
    e.preventDefault();
    if (!closing.current) {
      setDatePickerOpen(!datePickerOpen);
    }
  };

  const handleCalendarClose = () => {
    closing.current = true;
    setDatePickerOpen(false);

    setTimeout(() => {
      closing.current = false;
    }, 200);
  };

  const handleInputClick = () => {
    if (!datePickerOpen && !closing.current) {
      setDatePickerOpen(true);
    }
  };

  const CustomHeader = ({ date, decreaseMonth, increaseMonth }) => {
    const handleDecreaseMonth = () => {
      const newDate = actualDate.minus({ months: 1 });
      setActualDate(newDate);
      fetchExcludedDates(newDate);
      decreaseMonth();
    };

    const handleIncreaseMonth = () => {
      const newDate = actualDate.plus({ months: 1 });
      setActualDate(newDate);
      fetchExcludedDates(newDate);
      increaseMonth();
    };

    return (
      <div className={styles.header}>
        <div
          className={styles.date_arrow}
          style={{ transform: "rotate(90deg)" }}
          onClick={handleDecreaseMonth}
        >
          {/* No spaces around the glyph: they are text nodes with width, and they
              push it off the centre of the circle. */}
          <span>
            <i className="ico ico-chevron" />
          </span>
        </div>
        <p className={styles.current_month}>
          {date.toLocaleString({ month: "long", year: "numeric" })}
        </p>
        <div
          className={styles.date_arrow}
          style={{ transform: "rotate(-90deg)" }}
          onClick={handleIncreaseMonth}
        >
          {/* No spaces around the glyph: they are text nodes with width, and they
              push it off the centre of the circle. */}
          <span>
            <i className="ico ico-chevron" />
          </span>
        </div>
      </div>
    );
  };

  return (
    <div
      className={cls(styles.date_picker, {
        [styles.inline_date_picker]: inline,
        [styles.mobile_date_picker]: mobile,
      })}
    >
      <ReactDatePicker
        key={inline ? date.toFormat("yyyy-MM") : undefined}
        locale={i18n.language}
        selected={toPickerDate(date)}
        onChange={(value: Date): void => handleChange(value)}
        dateFormat="MMMM, d, yyyy"
        useWeekdaysShort={true}
        renderCustomHeader={({
          date: selDate,
          decreaseMonth,
          increaseMonth,
        }): React.ReactElement => (
          <CustomHeader
            increaseMonth={increaseMonth}
            decreaseMonth={decreaseMonth}
            date={DateTime.fromJSDate(selDate)}
          />
        )}
        customInput={mobile ? <CustomMobileInput /> : <CustomInput />}
        open={datePickerOpen}
        onClickOutside={handleCalendarClose}
        onCalendarClose={handleCalendarClose}
        onInputClick={handleInputClick}
        minDate={minDate}
        excludeDates={excludeDates}
        calendarClassName={cls(styles.day_picker, { [styles.inline]: inline })}
        calendarStartDay={preferences.firstDay}
        disabledKeyboardNavigation
        inline={inline}
      />
    </div>
  );
};

export default DatePicker;
